<script setup>
import { ref } from 'vue';

defineProps({
  setAuth: Function,
});

const expandedCategories = ref({});

const toggleCategory = (pillar) => {
  expandedCategories.value[pillar] = !expandedCategories.value[pillar];
};

const features = [
  {
    pillar: 'Portfolio Core',
    icon: 'pi-chart-line',
    items: [
      'Comprehensive Portfolio Dashboard',
      'Advanced Portfolio Analytics',
      'Sector & Asset Attribution',
      'Real-time Performance Tracking'
    ]
  },
  {
    pillar: 'Decision Frameworks',
    icon: 'pi-lightbulb',
    items: [
      'Personalized Model Portfolios',
      'Stock Analysis (Valuation + Technicals)',
      'Mutual Fund Recommender',
      'Advanced Stock Screeners'
    ]
  },
  {
    pillar: 'Planning & Discipline',
    icon: 'pi-calendar',
    items: [
      'Goal Tracking & Planning',
      'SIP Discipline Tools',
      'Comprehensive Financial Calculators',
      'Rebalancing Alerts'
    ]
  },
  {
    pillar: 'Education & Context',
    icon: 'pi-book',
    items: [
      'Premium Blogs & Video Library',
      'Quarterly Results Summaries',
      'Investment Guides & Playbooks',
      'Market Context & Commentary'
    ]
  }
];
</script>

<template>
  <section id="pricing" class="subscription-section">
    <div class="subscription-container">
      <!-- Section Header -->
      <div class="header-badge">🚀 Early Access Pricing</div>
      <h2 class="section-title">Start Your Investment Journey</h2>
      <p class="section-subtitle">
        Full access to all features. Transparent pricing. No hidden fees.
      </p>

      <!-- Two-Column Compact Layout -->
      <div class="pricing-content">
        <!-- Left: Unified Plan Journey Card -->
        <div class="plan-card">
          <h3 class="card-title">Plan Journey</h3>

          <!-- Trial Period -->
          <div class="plan-block trial">
            <div class="plan-header">
              <span class="plan-badge">Step 1</span>
              <span class="plan-label">Trial Period</span>
            </div>
            <div class="plan-pricing">
              <span class="price">₹500</span>
              <span class="period">for first 3 months</span>
            </div>
            <div class="price-subtext">~₹167/month</div>
          </div>

          <!-- Divider -->
          <div class="plan-divider">
            <i class="pi pi-arrow-right"></i>
          </div>

          <!-- Annual Subscription -->
          <div class="plan-block annual">
            <div class="plan-header">
              <span class="plan-badge recommended">Step 2</span>
              <span class="plan-label">Annual Subscription</span>
            </div>
            <div class="plan-pricing">
              <span class="price">₹6,000</span>
              <span class="period">per year thereafter</span>
            </div>
            <div class="price-subtext">₹500/month</div>
          </div>

          <!-- CTA Button -->
          <button class="cta-button" @click="setAuth && setAuth(true, 'signup')">Start with ₹500 Trial</button>

          <!-- Trust Badges - Compact Inline -->
          <div class="trust-badges">
            <span><i class="pi pi-shield"></i> Secure Payment</span>
            <span><i class="pi pi-refresh"></i> 7-Day Money Back</span>
            <span><i class="pi pi-lock"></i> Cancel Anytime</span>
          </div>
        </div>

        <!-- Right: Compact 2x2 Feature Grid -->
        <div class="features-card">
          <h3 class="card-title">Everything Included</h3>
          <div class="features-grid">
            <div
              v-for="featureGroup in features"
              :key="featureGroup.pillar"
              class="feature-category"
            >
              <h4 class="category-title">
                <i :class="['pi', featureGroup.icon]"></i>
                {{ featureGroup.pillar }}
              </h4>
              <ul class="feature-list">
                <li
                  v-for="(item, idx) in featureGroup.items.slice(0, 3)"
                  :key="idx"
                  class="feature-item"
                >
                  <i class="pi pi-check"></i>
                  {{ item }}
                </li>
              </ul>
              <button
                v-if="featureGroup.items.length > 3"
                :class="['expand-btn', { expanded: expandedCategories[featureGroup.pillar] }]"
                :aria-expanded="expandedCategories[featureGroup.pillar] || false"
                @click="toggleCategory(featureGroup.pillar)"
              >
                {{ expandedCategories[featureGroup.pillar] ? 'Show less' : 'View full list' }}
                <i :class="['pi', expandedCategories[featureGroup.pillar] ? 'pi-chevron-up' : 'pi-chevron-down']"></i>
              </button>
              <ul v-if="expandedCategories[featureGroup.pillar]" class="feature-list expanded">
                <li
                  v-for="(item, idx) in featureGroup.items.slice(3)"
                  :key="`extra-${idx}`"
                  class="feature-item"
                >
                  <i class="pi pi-check"></i>
                  {{ item }}
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <!-- Compact Disclaimer Strip -->
      <div class="disclaimer-strip">
        <span><i class="pi pi-times-circle"></i> Not a trading platform</span>
        <span class="separator">•</span>
        <span><i class="pi pi-times-circle"></i> Not a brokerage</span>
        <span class="separator">•</span>
        <span><i class="pi pi-times-circle"></i> No tips/prediction newsletter</span>
      </div>
    </div>
  </section>
</template>

<style scoped>
.subscription-section {
  padding: 48px 24px;
  background: linear-gradient(135deg, #f9fafb 0%, #ffffff 100%);
}

.subscription-container {
  max-width: 1200px;
  margin: 0 auto;
}

.header-badge {
  display: inline-block;
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  color: white;
  padding: 6px 16px;
  border-radius: 20px;
  font-size: 0.8125rem;
  font-weight: 700;
  text-align: center;
  margin: 0 auto 16px;
  display: block;
  width: fit-content;
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.25);
}

.section-title {
  font-size: 1.75rem;
  font-weight: 700;
  color: #1f2937;
  text-align: center;
  margin: 0 0 12px 0;
  letter-spacing: -0.02em;
}

.section-subtitle {
  font-size: 1rem;
  color: #6b7280;
  text-align: center;
  margin: 0 0 32px 0;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
}

/* Two-Column Compact Layout */
.pricing-content {
  display: grid;
  grid-template-columns: 0.9fr 1.1fr;
  gap: 24px;
  margin-bottom: 24px;
  align-items: stretch;
}

/* Left: Plan Journey Card */
.plan-card {
  background: white;
  border: 1px solid rgba(15, 23, 42, 0.1);
  border-radius: 14px;
  padding: 20px;
  box-shadow: 0 10px 30px rgba(2, 6, 23, 0.08);
  transition: transform 0.2s ease;
  display: flex;
  flex-direction: column;
}

.plan-card:hover {
  transform: translateY(-2px);
}

.card-title {
  font-size: 1rem;
  font-weight: 700;
  color: #1f2937;
  margin: 0 0 12px 0;
  text-align: center;
}

.plan-block {
  padding: 12px;
  border-radius: 10px;
  background: #f9fafb;
}

.plan-block.trial {
  background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%);
  border: 1px solid #93c5fd;
}

.plan-block.annual {
  background: linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%);
  border: 1px solid #6ee7b7;
}

.plan-header {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 8px;
}

.plan-badge {
  background: white;
  color: #4b5563;
  padding: 2px 6px;
  border-radius: 6px;
  font-size: 0.625rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.plan-badge.recommended {
  background: #047857;
  color: white;
  font-weight: 700;
}

.plan-label {
  font-size: 0.875rem;
  font-weight: 700;
  color: #1f2937;
}

.plan-pricing {
  display: flex;
  align-items: baseline;
  gap: 6px;
  margin-bottom: 4px;
}

.price {
  font-size: 1.75rem;
  font-weight: 800;
  color: #3b82f6;
  line-height: 1;
}

.plan-block.annual .price {
  color: #10b981;
}

.period {
  font-size: 0.75rem;
  color: #6b7280;
  font-weight: 500;
}

.price-subtext {
  font-size: 0.75rem;
  color: #6b7280;
  font-weight: 600;
}

.plan-divider {
  text-align: center;
  color: #3b82f6;
  font-size: 1.125rem;
  padding: 8px 0;
}

.cta-button {
  width: 100%;
  padding: 12px 16px;
  margin: 12px 0 10px;
  border: none;
  border-radius: 10px;
  font-size: 0.9375rem;
  font-weight: 700;
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  color: white;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.25);
}

.cta-button:hover {
  background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
  box-shadow: 0 6px 16px rgba(59, 130, 246, 0.35);
  transform: translateY(-1px);
}

/* Trust Badges - Compact Inline */
.trust-badges {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  padding-top: 10px;
  border-top: 1px solid #e5e7eb;
}

.trust-badges span {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 0.75rem;
  font-weight: 600;
  color: #047857;
}

.trust-badges i {
  color: #047857;
  font-size: 0.875rem;
}

/* Right: Features Card - 2x2 Grid */
.features-card {
  background: white;
  border: 1px solid rgba(15, 23, 42, 0.1);
  border-radius: 14px;
  padding: 24px;
  box-shadow: 0 10px 30px rgba(2, 6, 23, 0.08);
  transition: transform 0.2s ease;
  display: flex;
  flex-direction: column;
}

.features-card:hover {
  transform: translateY(-2px);
}

.features-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
}

.feature-category {
  border-bottom: 1px solid #f3f4f6;
  padding-bottom: 16px;
}

.feature-category:nth-child(3),
.feature-category:nth-child(4) {
  border-bottom: none;
  padding-bottom: 0;
}

.category-title {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.875rem;
  font-weight: 700;
  color: #1e40af;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  margin: 0 0 10px 0;
}

.category-title i {
  font-size: 0.875rem;
}

.feature-list {
  list-style: none;
  padding: 0;
  margin: 0 0 8px 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.feature-item {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  font-size: 0.8125rem;
  color: #4b5563;
  line-height: 1.4;
}

.feature-item i {
  color: #047857;
  font-size: 0.75rem;
  margin-top: 2px;
  flex-shrink: 0;
}

.expand-btn {
  display: flex;
  align-items: center;
  gap: 4px;
  background: none;
  border: none;
  color: #1e40af;
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;
  padding: 4px 0;
  transition: color 0.2s ease;
}

.expand-btn:hover {
  color: #1e40af;
}

.expand-btn i {
  font-size: 0.625rem;
}

.feature-list.expanded {
  margin-top: 6px;
}

/* Compact Disclaimer Strip */
.disclaimer-strip {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 12px;
  padding: 12px 16px;
  background: #f9fafb;
  border-radius: 10px;
  border: 1px solid #e5e7eb;
}

.disclaimer-strip span {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 0.8125rem;
  font-weight: 500;
  color: #6b7280;
}

.disclaimer-strip i {
  color: #9ca3af;
  font-size: 0.75rem;
}

.separator {
  color: #d1d5db;
  font-weight: 400;
}

/* Responsive Design */
@media (max-width: 1024px) {
  .pricing-content {
    grid-template-columns: 1fr;
    gap: 20px;
  }

  .features-grid {
    grid-template-columns: 1fr;
  }

  .feature-category {
    border-bottom: 1px solid #f3f4f6;
    padding-bottom: 16px;
  }

  .feature-category:last-child {
    border-bottom: none;
  }
}

@media (max-width: 768px) {
  .subscription-section {
    padding: 32px 20px;
  }

  .section-title {
    font-size: 1.5rem;
  }

  .section-subtitle {
    font-size: 0.9375rem;
  }

  .plan-card,
  .features-card {
    padding: 16px;
  }

  .card-title {
    font-size: 0.9375rem;
    margin: 0 0 10px 0;
  }

  .plan-block {
    padding: 10px;
  }

  .price {
    font-size: 1.5rem;
  }

  .plan-label {
    font-size: 0.8125rem;
  }

  .period {
    font-size: 0.6875rem;
  }

  .price-subtext {
    font-size: 0.6875rem;
  }

  .cta-button {
    padding: 10px 14px;
    font-size: 0.875rem;
  }

  .trust-badges {
    flex-direction: column;
    gap: 8px;
  }

  .category-title {
    font-size: 0.8125rem;
  }

  .feature-item {
    font-size: 0.75rem;
  }

  .disclaimer-strip {
    flex-direction: column;
    gap: 8px;
    text-align: center;
  }

  .separator {
    display: none;
  }
}
</style>
